interface ISiteCard {

    Model: ISiteModel;
    ModelData: ISiteModel;
    Container: ISiteContainer;

    remove(): Promise<void>;
    post(request: IActionTypePost): Promise<boolean>;
    request(request: IActionTypeRequest): Promise<boolean>;
    isActive(): boolean;
    isLarge(): boolean;
    isRemoved(): boolean;
    makeActive(): boolean;
    makeInactive(): void;
    keyPress(keyCodes: number[]): void;
    back(): Promise<void>;

    refresh(): Promise<boolean>;

    makeFocusableActive(focusable: ISiteFocusable): this;
    makePrevFocusableActive(focusable: ISiteFocusable): this;
    makeNextFocusableActive(focusable: ISiteFocusable): this;

    addValidation(path: string[], validation: ISiteValidation): void;
    clearValidation(): void;

    next<T extends IQueryElement>(): T | null;
    prev<T extends IQueryElement>(): T | null;
    containsClass(string: string): boolean;
    position(): ClientRect | null;
    style(): CSSStyleDeclaration | undefined;
    getAttr(attr: string): string | null;
    setAttr(attr: string, content: string): this;

    nextAll<T extends IQueryElement>(func: (item: T) => boolean | void): this;
    trigger(val: string, bubble: boolean): this;
}

class SiteCardRefreshEvent 
    extends CancellableEvent {
}

class SiteCard
    extends QueryElement<HTMLElement>
    implements ISiteCard {

    private _observerEvent: (e: CustomEvent) => any;
    private _observerChange: (e: CustomEvent) => any;

    private _request?: ISiteRequest;
    private _title: string;
    private _model: ISiteModel;
    private _modelData: ISiteModel;
    private _container: ISiteContainer;
    private _focusables: ISiteFocusable[] = [];
    private _keyPressTimer?: number;
    private _backAction?: IActionType;
    private _currentSize: string;
    private readonly _defaultSize: SiteResponseCardSizes;
    private readonly _labels: ISiteLabel[];

    get Title() { return this._title; }
    get Model() { return this._model; }
    get ModelData() { return this._modelData; }
    get SiteConfig() { return this._container.SiteConfig; }
    get Container() { return this._container; }

    constructor(container: ISiteContainer, html: string, response: ISiteResponseCard, request?: ISiteRequest) {
        super(document.createElementFromHTML(html));

        this._request = request;

        // Check if we got a model, else set an empty object
        if (response.model == null)
            response.model = {};
        if (response.metaData == null)
            response.metaData = {};    

        // Wrap when needed!
        this._model = hasInterface<ISiteModel>(response.model, "ISiteModel") ?
            response.model : new SiteModel(response.model);
        this._modelData = hasInterface<ISiteModel>(response.metaData, "ISiteModel") ?
            response.metaData : new SiteModel(response.metaData);
        this._title = response.options.title || "";

        this._container = container;

        this._backAction = response.options.backAction;

        this.prepend(new SiteCardMenu(this, response.options.cardItems || []))
            .on("click", () => container.makeCardActive(this));
        
        this._defaultSize = response.options.size ?? "Normal";
        this._currentSize = this._defaultSize;
        this.setSize();

        this.query("h2").first(a => {
            a.setHtml(this._title);
            a.on("click", async () => await container.removeNextCards(this));
        });

        // Statements:
        this.query("div[data-type='foreach']").createAll<IQueryElement, HTMLElement>(a => new SiteStatementForeach(this, a));
        this.query("div[data-type='for']").createAll<IQueryElement, HTMLElement>(a => new SiteStatementFor(this, a));
        this.query("div[data-type='if']").createAll<IQueryElement, HTMLElement>(a => new SiteStatementIf(this, a));


        // Loop through all labels
        this.query("label>[data-type]").all(a => {
            let parent = a && a.parent();
            if (parent !== null)
            {
                parent.setAttr("data-config", a.getAttr("data-config") || "");
                parent.setAttr("data-type", a.getAttr("data-type") || "");
            }
        });

        // Labels
        let labels: ISiteLabel[] = [];

        labels.push(...this.query("label[data-type='input']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelInput(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='color']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelInput(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='input-multi']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelInputLanguage(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='dropdown']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelDropdown(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='dropdown-multi']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelDropdownMulti(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='checkbox']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelCheckbox(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='datetime']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelDateTime(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='fido']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelFido(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='calculator']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelCalculator(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='decimal']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelDecimal(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='signature']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelSignature(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='upload']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelUpload(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='unit']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelUnit(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='coordinates']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelCoordinates(this, this.Model, this.ModelData, a)));
        labels.push(...this.query("label[data-type='markdown']").createAll<ISiteLabel, HTMLElement>(a => new SiteLabelMarkdown(this, this.Model, this.ModelData, a)));
        this._labels = labels;
        
        for (let label of labels) {
            let input = label.getInput();
            if (input !== null)
                label.init(input);
        }

        // Static data
        this.query("[data-type='bind']").createAll<IQueryElement, HTMLElement>(a => new SiteElementBind(this, a));
        this.query("[data-type='bindClass']").createAll<IQueryElement, HTMLElement>(a => new SiteElementBindClass(this, a));
        this.query("[data-type='image']").createAll<IQueryElement, HTMLImageElement>(a => new SiteElementImage(this, a));
        this.query("[data-type='timer']").createAll<IQueryElement, HTMLElement>(a => new SiteElementTimer(this, a));
        this.query("[data-type='actionButton']").createAll<IQueryElement, HTMLElement>(a => new SiteElementBindActionButton(this, a));
        this.query("[data-type='collapsable']").createAll<IQueryElement, HTMLElement>(a => new SiteElementCollapsable(this, a));
        this.query("[data-type='action']").createAll<IQueryElement, HTMLElement>(a => new SiteActionButton(this, {
            action: this.Model.get(SiteModel.split(a.getAttribute("data-path") || ""))
        }, a));
        this.query(".clickable-images").createAll<IQueryElement, HTMLElement>(a => new SiteElementImages(this, a));
        this.query("a[target='action']").createAll<IQueryElement, HTMLElement>(a => new SiteLink(this, {
            action: a.getAttribute("href") || ""
        }, a));
        
        // Elements
        this.query("[data-type='chart']").createAll<IQueryElement, HTMLCanvasElement>(a => new SiteElementChart(this, a));
        this.query("[data-type='route']").createAll<IQueryElement, HTMLElement>(a => new SiteElementRoute(this, a));
        this.query("[data-type='progress']").createAll<IQueryElement, SVGElement>(a => new SiteElementProgress(this, a));
        this.query("[data-type='search']").createAll<IQueryElement, HTMLElement>(a => new SiteElementSearch(this, a));
        this.query("[data-type='navigation']").createAll<IQueryElement, HTMLElement>(a => new SiteElementNavigation(this, a));
        this.query("[data-type='html-view']").createAll<IQueryElement, HTMLElement>(a => new SiteElementHtmlView(this, a));
        this.query("[data-type='chat']").createAll<IQueryElement, HTMLElement>(a => new SiteElementChat(this, this.Model, a));
        this.query("[data-type='focus']").createAll<IQueryElement, HTMLElement>(a => new SiteElementFocus(this, a));
        
        // Views
        this.query("[data-type='view-file']").createAll<IQueryElement, HTMLElement>(a => new SiteElementViewFile(this, a));

        let x = 1;
        this._focusables = this.query("[data-instance][if-ISiteFocusable=true]")
            .all<ISiteFocusable, ISiteFocusable>(a => {
                let tabIndex = a.getAttr("data-tab-index");
                if (tabIndex === null)
                    a.setAttr("data-tab-index", (x++).toString());
                
                return a;
            })
            .sort((a, b) => {
                let an = Number(a.getAttr('data-tab-index') || 0);
                let bn = Number(b.getAttr('data-tab-index') || 0);

                if (an === bn)
                    return Number(a.getAttr('data-instance')) - Number(b.getAttr('data-instance'));
                else
                    return an - bn;
            });
        
        this._observerEvent = this.on("qts.observer.event", (e: CustomEvent) => {
            let details = e.detail as IMessage;
            let eventSort: SiteResponseCardObserverEvents | undefined;
            switch (details.sort) {
                case "Barcode":
                    eventSort = "Scan";
                    break;
                case "Image":
                    eventSort = "Photo";
                    break;
            }

            if (eventSort === undefined)
                return;
            
            let site = Site.getSite();
            // Close or execute modals when an event is triggered
            site.getModals().forEach((a) => {
                if (a instanceof SiteImageModal) {
                    if (eventSort == "Scan")
                        a.execute();
                }

                a.remove();
                // Close parent
                let parent = a.parent<IQueryElement>();
                parent && parent.remove();
            });
        
            for (let observer of response.observers.filter(
                a => a.sort == "Event"
                     && (<ISiteResponseCardObserverEvent>a).event == eventSort)
            ) {
                let event = <ISiteResponseCardObserverEvent>observer;
                let model = this.Model.get(SiteModel.split(event.path));

                // Get last action:
                let currentAction = event.action;
                while (currentAction.nextAction) {
                    currentAction = currentAction.nextAction as IActionTypeRequest;
                }

                if (eventSort == "Scan") {
                    let barcodeDetails = details as BarcodeMessage;

                    if (model === undefined || model["$type"] === undefined) {
                        currentAction.actionLink.model = {
                            "$type": `DirectCode.Modules.MVC.Models.BarcodeEventModel, DirectCode.Session.MVC`,
                            "barcode": barcodeDetails.barcode
                        };
                    }
                    else {
                        currentAction.actionLink.model = {
                            "$type": `DirectCode.Modules.MVC.Models.BarcodeEventModel\`1[[${model["$type"]}]], DirectCode.Session.MVC`,
                            "model": model,
                            "barcode": barcodeDetails.barcode
                        };
                    }
                    // Set ID if we had one
                    if (request != null && request.request.id) {
                        currentAction.actionLink.actionInfo.id = request.request.id
                    }
                    
                    let action = SiteAction.create(this, event.action);
                    action && action.execute();
                }

                if (eventSort == "Photo") {
                    let imageDetails = details as ImageMessage;
                    if (imageDetails.image == null)
                        return;
                    
                    currentAction.actionLink.model = {
                        "$type": `DirectCode.Modules.MVC.Models.ImageEventModel\`1[[${model["$type"]}]], DirectCode.Session.MVC`,
                        "model": model,
                        "data": imageDetails.image
                    };

                    site.showModal(this, {
                        sort: "Image",
                        image: imageDetails.image,
                        action: event.action
                    } as ISiteImageModal) as SiteImageModal;
                }
            }
        });
        this._observerChange = this.on("qts.observer.change", async (e: CustomEvent) => {
            if(e.detail instanceof ObserverChangeEvent) {
                for(let observer of response.observers.filter(a => a.sort == "Change")) {
                    let change = <ISiteResponseCardObserverChange>observer;
                    
                    if (e.detail.types.some(a => a == change.type)) {
                        await this.refresh();
                        break;
                    }
                }
            }
        });
    }
    setSize(size?: SiteResponseCardSizes) {
        this.removeClass("card-small");
        this.removeClass("card-large");
        this.removeClass("card-normal2");
        this.removeClass("card-normal3");

        if (size == null)
            size = this._defaultSize;

        switch(size) {
            case "Small":
                this.addClass("card-small");
                break;
            case "Normal2":
                this.addClass("card-normal2");
                break;
            case "Normal3":
                this.addClass("card-normal3");
                break;
            case "Large":
                this.addClass("card-large");
                break;
        }

        if (size != this._currentSize) {
            this._currentSize = size;
            this._container.redraw();
        }
    }
    async back() {
        let actionType : IActionType = this._backAction ?? {
            type: "Close"
        };
        let action = SiteAction.create(this, actionType);
        await action?.execute();
    }

    async start() {
        this.trigger("qts.started", false);

        if (!Site.isMobile()) {
            this.addClass("in-left");
            await delay(200);
            this.removeClass("in-left");
        }
    }
    async refresh() {
        let event = new SiteCardRefreshEvent();
        this.trigger("qts.refresh", false, event);

        if(event.cancelled == true)
            return true;

        if(this._request)
        {
            let request = this._request;
            request.options = {
                override: true
            };
            
            await this.Container.request(this, request);
            return true;
        }
        
        return false;
    }
    async keyPress(keyCodes: number[]) {
        switch(keyCodes[0]) {
            case 17:
                if(keyCodes.length == 1) {
                    this._keyPressTimer && window.clearTimeout(this._keyPressTimer);
                    this._keyPressTimer = window.setTimeout(() => this.removeClass("key-show"), 500);
                    this.addClass("key-show");
                } else {
                    this.removeClass("key-show");
                    
                    let toClick = this.query(`[data-key="CTRL+${String.fromCharCode(keyCodes[1])}"]`).first();
                    toClick && toClick.trigger("click", true);
                }
                break;
            case 13:
                let toClick = this.query(`[data-key="ENTER"]`).first();
                toClick && toClick.trigger("click", true);
                break;
        }
    }
    post(request: IActionTypePost): Promise<boolean> {
        let path = request.path && SiteModel.split(request.path) || [];
        let model = request.actionLink.model;

        if (request.setModel)
            model = this.Model.get(path);
        
        // Convert model, when not null or undefined
        if (model != null) {
            return this._container.post({
                request: request.actionLink.actionInfo,
                path: path,
                model: JSON.stringify(model) 
            });
        }
        return this._container.post({
            request: request.actionLink.actionInfo
        });
    }
    request(request: IActionTypeRequest): Promise<boolean> {
        return new SiteCardRequestCommand(this, request)
            .execute();
    }

    isLarge(): boolean {
        return this.containsClass("card-large");
    }

    addValidation(path: string[], validation: ISiteValidation) {
        for (let property in validation.property) {
            this._focusables
                .filter(a => a.getAttr("if-ISiteLabel"))
                .filter(a => (<ISiteLabel>a).path.join(".") == path.concat(property).join("."))
                .map(a => (<ISiteLabel>a).setError(validation.property[property].first() || ""));
        }
    }
    clearValidation() {
        this._focusables
            .filter(a => a.getAttr("if-ISiteLabel"))
            .filter(a => a.containsClass("error"))
            .map(a => (<ISiteLabel>a).clearError());
    }

    getActiveFocusable() {
        return this.query("[if-ISiteFocusable].active")
            .first<ISiteFocusable>();
    }
    getInactiveFocusable() {
        return this.query("[if-ISiteFocusable].inactive")
            .first<ISiteFocusable>();
    }
    isLastFocusable(focusable: ISiteFocusable) {
        return this._focusables.indexOf(focusable) + 1 == this._focusables.length;
    }
    makePrevFocusableActive(focusable: ISiteFocusable) : this {
        if (!focusable.containsClass("active"))
            return this;

        let target = focusable; 
        let index = this._focusables.indexOf(focusable);
        // Find prev item
        do {
            index -= 1;
            if (index < 0)
                index = this._focusables.length - 1;

            target = this._focusables[index];
        } while(!target.isVisible())

        return this.makeFocusableActive(target);
    }
    makeNextFocusableActive(focusable: ISiteFocusable): this {
        if (!focusable.containsClass("active"))
            return this;

        let target = focusable;
        let index = this._focusables.indexOf(focusable);
        // Find next item
        do {
            index += 1;
            if (index > this._focusables.length - 1)
                index = 0;

            target = this._focusables[index];
        } while (!target.isVisible())

        return this.makeFocusableActive(target);
    }
    makeFocusableActive(focusable: ISiteFocusable) {
        if (focusable.containsClass("active"))
            return this;

        // Make active focusable inactive:
        let activeFocusable = this.getActiveFocusable();
        activeFocusable && activeFocusable.makeInactive();

        // Make current label active
        this.trigger("qts.label", false, focusable);
        focusable.makeActive();
            
        return this;
    }
    getLabel(path: string) {
        var splitPath = SiteModel.split(path);
        var label = this._labels.filter(a => SiteModel.equals(a.path, splitPath));
        if (label.length > 0)
            return label[0];
        else
            return null;
    }

    isActive() {
        return this.containsClass("active");
    }
    makeActive() {
        if (this.isActive())
            return false;

        this.addClass("active");
        this.trigger("qts.active", false);

        // Remove inactive class
        let inactive = this.getInactiveFocusable();
        if (inactive)
            inactive.removeClass("inactive")
        
        // If no active focusable,
        if (!this.getActiveFocusable()) {
            if (inactive) {
                // try to revive a previous active field
                this.makeFocusableActive(inactive);
            } else if (this._focusables.length > 0) {
                // else make the first field focusable
                this.makeFocusableActive(this._focusables[0]);
            }
        }

        return true;
    }

    makeInactive() {
        if (!this.isActive())
            return;

        // Make active label inactive:
        let activeFocusable = this.getActiveFocusable();
        if (activeFocusable) {
            activeFocusable.makeInactive();
            activeFocusable.addClass("inactive");
        }

        this.removeClass("active");
        this.trigger("qts.inactive", false);
    }
    isRemoved() {
        return this.containsClass("out-left") || super.isRemoved();
    }

    async remove(removeInner = true) {
        this.off("qts.observer.event", this._observerEvent);
        this.off("qts.observer.change", this._observerChange);


        if (!Site.isMobile()) {
            this.addClass("out-left");
            await delay(200);
        }

        await super.remove(removeInner);
    }
} 